import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink, Phone, X } from "lucide-react";
import { canDialFromDevice, stripTel } from "@/lib/tel";
import { trackCallClick } from "@/lib/ga4";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { WidgetCommunicationService } from "@/services/integrations/widget/WidgetCommunicationService";
import { isDarkBackground, widgetBackgroundToCss } from "@/lib/widgetBackgroundPresets";
import { GenieTextChatOverlayWeb } from "./components/GenieTextChatOverlayWeb";

import type { EmbedLiveAgentLayoutProps } from "@/types/embed-live-agent-layout";

/** Popup width from which the avatar and chat sit side by side. */
export const EMBED_WIDE_LAYOUT_BREAKPOINT_PX = 700;

export const EmbedLiveAgentLayout = ({
  children,
  actionRow,
  chatOpen,
  chatOnly = false,
  brandColor,
  secondaryColor,
  agentName,
  agentDescription,
  phoneNumber,
  hideWelcomeText,
  customAvatarUrl,
  backgroundColor,
  background,
  headerLogoSize = 36,
  headerStyle,
  chatStyle,
  actionButtonStyle,
  urlName,
  messages,
  isConnected,
  isConnecting,
  isSpeaking,
  isAllAudioMuted,
  onToggleAllAudioMute,
  isTextOnlyMode,
  inlineMessage,
  onChangeInlineMessage,
  onSend,
  startedFromText,
  onCloseChat,
  onEndChat,
  onEndConversation,
  attachments = [],
  onRemoveAttachment,
  onOpenFilePicker,
  onUpdateAttachmentAnalysis,
  imageUploadPendingCount,
  imageUploadFailedCount,
  imageUploadSaving,
  onRetryImageUploads,
  headerVoiceControls,
  inlineCaptureSlot,
  onExitChat,
  hideAttribution,
}: EmbedLiveAgentLayoutProps) => {
  const accent = secondaryColor || "#FF4DB8";
  const fullPageUrl = urlName ? `${window.location.origin}/live-agent/${encodeURIComponent(urlName)}${chatOnly ? "?embed=true&mode=chat" : ""}` : null;
  const bodyStyle = widgetBackgroundToCss(background, backgroundColor || "white");
  // Same test as the website chat: a dark popup body gets light chat inks.
  const darkSurface = isDarkBackground(background ?? (backgroundColor ? { type: "solid", color: backgroundColor } : null));
  const logoImgSize = Math.round(headerLogoSize * 0.61);
  const headerStart = headerStyle?.color || brandColor;
  const headerEnd = headerStyle?.color2 || accent;
  const headerBg: React.CSSProperties = headerStyle?.transparent
    ? { background: "transparent" }
    : { background: `linear-gradient(135deg, ${headerStart} 0%, ${headerEnd} 100%)` };

  const handleClose = () => {
    WidgetCommunicationService.sendToParent("helpgenie:close");
  };

  // Wide-enough popups (e.g. the xl 900px preset, or any custom iframe size a
  // client sets via the raw embed code) show the avatar and chat panels side
  // by side instead of swapping between them — same idea as the standalone
  // live-agent desktop layout. Detected via ResizeObserver on this root
  // element rather than threaded down as a prop, since the actual rendered
  // width can't be known from the popupSize preset alone (custom iframes).
  // 700px = the 340px avatar column (wide enough for the action row to fit
  // without wrapping/overflowing) + a ~340px minimum usable chat column +
  // the divider, so the two-column mode never renders more cramped than the
  // narrow single-column mode already looks.
  const rootRef = useRef<HTMLDivElement>(null);
  const [isWideLayout, setIsWideLayout] = useState(false);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      setIsWideLayout(entry.contentRect.width >= EMBED_WIDE_LAYOUT_BREAKPOINT_PX);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className="flex h-full min-h-0 w-full flex-col overflow-hidden"
      style={bodyStyle}
    >
      <header
        className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
        style={headerBg}
      >
        <div
          className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/25"
          style={{ width: headerLogoSize, height: headerLogoSize }}
        >
          {customAvatarUrl ? (
            <img src={customAvatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <HelpGenieLogo size={logoImgSize} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight text-white">
            {agentName || "Help Genie"}
          </p>
          {!hideWelcomeText && (
            <p className="truncate text-[11px] leading-tight text-white/85">
              {agentDescription || (chatOnly ? "How can we help?" : "Voice or text — your call")}
            </p>
          )}
        </div>

        {!chatOnly && chatOpen && !isWideLayout && onExitChat && (
          <button
            type="button"
            onClick={onExitChat}
            aria-label="Exit chat and return to start"
            title="Back to start"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/35"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        )}

        {canDialFromDevice(phoneNumber) && (
          <a
            href={`tel:${stripTel(phoneNumber)}`}
            onClick={() => trackCallClick("embed_header_call")}
            aria-label={`Call ${agentName || "this Genie"} at ${phoneNumber}`}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/35"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={2.25} />
          </a>
        )}

        {fullPageUrl && (
          <a
            href={fullPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in full page"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/35"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.25} />
          </a>
        )}

        {/* Close asks the host page to hide the frame; opened directly in a
            tab there is no host to ask. */}
        {WidgetCommunicationService.isInIframe() && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close widget"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/35"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        )}
      </header>

      <main className="flex min-h-0 flex-1 flex-col" style={bodyStyle}>
        {(() => {
          const chatHeaderBar = isConnected && headerVoiceControls && (
            <div
              className="flex flex-shrink-0 items-center justify-between gap-2 px-3 py-2"
              style={bodyStyle}
            >
              <div className="min-w-0 flex-1">{headerVoiceControls}</div>
            </div>
          );

          const chatOverlay = (
            <GenieTextChatOverlayWeb
              brandColor={brandColor}
              secondaryColor={secondaryColor}
              agentName={agentName}
              messages={messages}
              inlineMessage={inlineMessage}
              onChangeInlineMessage={onChangeInlineMessage}
              onSend={onSend}
              isConnecting={isConnecting}
              isSpeaking={isSpeaking}
              isTextOnlyMode={isTextOnlyMode}
              isAllAudioMuted={isAllAudioMuted}
              onToggleAllAudioMute={onToggleAllAudioMute}
              startedFromText={startedFromText}
              onCloseOverlay={onCloseChat}
              onEndChat={onEndChat}
              attachments={attachments}
              onRemoveAttachment={onRemoveAttachment}
              onOpenFilePicker={onOpenFilePicker}
              onUpdateAttachmentAnalysis={onUpdateAttachmentAnalysis}
              imageUploadPendingCount={imageUploadPendingCount}
              imageUploadFailedCount={imageUploadFailedCount}
              imageUploadSaving={imageUploadSaving}
              onRetryImageUploads={onRetryImageUploads}
              inDrawer
              hideHeader
              fontSizeOverride={12}
              voiceConnected={isConnected}
              onEndConversation={onEndConversation}
              backgroundStyle={bodyStyle}
              darkSurface={darkSurface}
              logoUrl={customAvatarUrl}
              inputBackgroundColor={chatStyle?.inputBackgroundColor}
              chatStyle={chatOnly ? { ...chatStyle, subtitleText: chatStyle?.subtitleText || "Type a question to get started." } : chatStyle}
              actionButtonStyle={actionButtonStyle}
              inlineCaptureSlot={inlineCaptureSlot}
            />
          );

          const avatarView = (
            <div className="flex flex-1 flex-col items-center justify-center px-4 py-3 text-center">
              {children}
              {actionRow ? <div className="mt-3 w-full">{actionRow}</div> : null}
            </div>
          );

          if (chatOpen && isWideLayout && !chatOnly && !isTextOnlyMode) {
            // Wide popup: avatar/CTA column stays visible alongside chat
            // instead of being replaced by it, both columns equal width —
            // no divider between them, they read as one continuous surface.
            return (
              <div className="flex min-h-0 flex-1 flex-row">
                <div className="flex flex-1 min-w-0 flex-col overflow-hidden">{avatarView}</div>
                {/* The controls under the avatar cover this column too, so
                    the chat header's copy of them is left out here. */}
                <div className="flex flex-1 min-w-0 flex-col">{chatOverlay}</div>
              </div>
            );
          }

          if (chatOpen) {
            return (
              <>
                {chatHeaderBar}
                {chatOverlay}
              </>
            );
          }

          return avatarView;
        })()}
      </main>

      {!hideAttribution && (
        <footer className="flex flex-shrink-0 items-center justify-center gap-2 border-t border-gray-100 px-4 py-1.5">
          <HelpGenieLogo size={14} />
          {fullPageUrl ? (
            <a
              href={fullPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-medium transition-opacity hover:opacity-70"
              style={{ color: brandColor }}
            >
              Made magical by Help Genie
            </a>
          ) : (
            <span className="text-[11px] font-medium" style={{ color: brandColor }}>
              Made magical by Help Genie
            </span>
          )}
        </footer>
      )}
    </div>
  );
};

export default EmbedLiveAgentLayout;
